/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { Edit, FileText, Save } from 'lucide-react';
import { FC, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { useMutate, useUser } from '@/net/commons';

interface InstructionsTabProps {
    storySlug?: string | undefined;
    instructionsData?: {
        instructions: string;
    };
    readonly?: boolean;
    onSave?: (instructions: string) => void;
}

export const InstructionsTab: FC<InstructionsTabProps> = ({
    storySlug,
    instructionsData = { instructions: '' },
    readonly = false,
    onSave,
}) => {
    const { user } = useUser();

    const isAdmin = user?.isSuperUser;

    const isImpersonated = user?.impersonatedBy;

    const hasAdminPermissions = isAdmin || isImpersonated;

    const { instructions } = instructionsData ?? { instructions: null };
    const { projectSlug, suiteSlug } = useParams();

    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState('');

    const { trigger: updateInstruction, isMutating } = useMutate(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/story/${storySlug}/instructions`,
        {
            revalidate: true,
            method: 'POST',
        },
    );

    const handleEdit = () => {
        setDraft(instructions || '');
        setIsEditing(true);
    };

    const handleSave = async () => {
        try {
            await updateInstruction({
                instructions: draft,
            });
            onSave?.(draft);
            setIsEditing(false);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    };

    if (isEditing) {
        return (
            <div className="">
                <div className="flex items-center gap-2 mb-4">
                    <FileText className="text-slate-600 w-5" />
                    <h3 className="text-lg font-medium text-slate-800">Instructions</h3>
                </div>
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                    }}
                    className="w-full h-[400px] p-2 border rounded"
                    placeholder="Enter markdown instructions here..."
                />
                {hasAdminPermissions && !readonly && (
                    <div className="flex justify-end mt-2">
                        <Button onClick={handleSave} variant="default">
                            <Save className="h-4 w-4 mr-2" />
                            Save
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="">
            <div className="flex items-center gap-2 mb-4">
                <FileText className="text-slate-600 w-5" />
                <h3 className="text-lg font-medium text-slate-800">Instructions</h3>
            </div>
            {instructions ? (
                <>
                    <div className="prose prose-slate max-w-none dark:prose-invert bg-white rounded p-2">
                        <ReactMarkdown
                            components={{
                                h1: ({ children }) => <h1 className="text-3xl font-bold mb-4 mt-6">{children}</h1>,
                                h2: ({ children }) => <h2 className="text-2xl font-semibold mb-3 mt-5">{children}</h2>,
                                h3: ({ children }) => <h3 className="text-xl font-medium mb-2 mt-4">{children}</h3>,
                                p: ({ children }) => <p className="mb-4 leading-relaxed">{children}</p>,
                                ul: ({ children }) => (
                                    <ul className="list-disc list-inside mb-4 space-y-2">{children}</ul>
                                ),
                                ol: ({ children }) => (
                                    <ol className="list-decimal list-inside mb-4 space-y-2">{children}</ol>
                                ),
                                li: ({ children }) => <li className="ml-4">{children}</li>,
                                a: ({ href, children }) => (
                                    <a
                                        href={href}
                                        className="text-purple-600 hover:text-purple-800 underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {children}
                                    </a>
                                ),
                                code: ({ children }) => (
                                    <pre className="bg-slate-100 p-4 my-1 rounded-lg overflow-x-auto">
                                        <code className="text-sm font-mono">{children}</code>
                                    </pre>
                                ),
                                blockquote: ({ children }) => (
                                    <blockquote className="border-l-4 border-slate-200 pl-4 italic">
                                        {children}
                                    </blockquote>
                                ),
                                hr: () => <hr className="my-8 border-slate-200" />,
                            }}
                        >
                            {instructions}
                        </ReactMarkdown>
                    </div>
                    {hasAdminPermissions && !readonly && (
                        <div className="flex justify-end mt-2">
                            <Button onClick={handleEdit} variant="outline">
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                            </Button>
                        </div>
                    )}
                </>
            ) : (
                <div className="flex flex-col items-center gap-4 p-8">
                    <p className="text-slate-500">No instructions available</p>
                    {hasAdminPermissions && !readonly && (
                        <Button onClick={handleEdit} variant="default">
                            <Edit className="h-4 w-4 mr-2" />
                            Add Instructions
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};
